import { titleCase } from "../utils/index.js";
import {
	products, labs, MAGNET_INDICATORS, EU_COUNTRIES, EFSA_COUNTRIES,
	DISASTER_EVENT_TYPES, LCA_INDICATORS, ENV_IMPACT_CATEGORIES,
	SOCIAL_INDICATORS, SOCIAL_ENDPOINT_CATEGORIES, SOCIAL_INDICATOR_CATEGORY_MAP,
} from "../utils/constants/index.js";
import { routes } from "../routes.js";

// Build search index once at module load - all source data is static
const buildSearchIndex = () => {
	// Standard product entries
	const productEntries = products.map((product) => ({
		type: "product",
		name: product.text,
		link: `/products?selected=${product.value}`,
		product,
	}));

	// Map entries - include ALL products, not just top-level ones
	const mapEntries = products
		.filter((product) => !product.subheader)
		.map((product) => ({
			type: "map",
			name: `${product.text} Map`,
			link: `/map?selected=${product.value}`,
			product,
			value: product.value,
		}));

	// Map entries for specific subproducts
	const subProductMapEntries = products.flatMap((product) => {
		if (product.subheader && Array.isArray(product.prices.products)) {
			return product.prices.products.map((subProduct) => ({
				type: "map",
				name: `${subProduct.toLocaleLowerCase()} (${product.text}) Map`,
				link: `/map?selected=${product.value}`,
				product,
				value: product.value,
			}));
		}
		return [];
	});

	// Lab entries
	const labEntries = labs.flatMap((lab) => lab.products.map((product) => ({
		type: "lab",
		name: `${lab.title} (Relevant: ${product})`,
		link: lab.path,
		lab,
		productName: product,
	})));

	// General Screen entries from routes.js
	const sidebarPaths = new Set(["home", "products", "Map", "contaminants", "socialimpact", "aiassistant", "disasters", "ews"]);
	const routeEntries = routes
		.filter((route) => route.path && sidebarPaths.has(route.path))
		.map((route) => {
			const type = "screen";
			let name = titleCase(route.path.replace("-", " "));

			if (route.path === "aiassistant") name = "AI Assistant";
			if (route.path === "socialimpact") name = "Social Impact";
			if (route.path === "dataview") name = "Data View";
			if (route.path === "Map") name = "Map View";

			return {
				type,
				name,
				link: `/${route.path}`,
			};
		});

	// Filter out duplicates
	const existingLinks = new Set([...productEntries, ...mapEntries, ...subProductMapEntries, ...labEntries].map((e) => e.link));
	const uniqueRouteEntries = routeEntries.filter((route) => !existingLinks.has(route.link));

	// Social Impact: Indicator entries
	const indicatorEntries = MAGNET_INDICATORS.flatMap((category) => category.options.map((option, optIndex) => ({
		type: "indicator",
		name: option.text,
		link: "/socialimpact",
		indicator: option,
		category: category.label,
		description: category.desc?.[optIndex] || "Social impact indicator",
	})));

	// Social Impact: Category entries
	const categoryEntries = MAGNET_INDICATORS.map((category) => ({
		type: "category",
		name: `${category.label} (Category)`,
		link: "/socialimpact",
		category: category.label,
		description: `Social impact category with ${category.options.length} indicators`,
	}));

	// Social Impact: EU country entries
	const socialImpactCountryEntries = EU_COUNTRIES
		.filter((country) => country.value !== "EU")
		.map((country) => ({
			type: "socialimpact-country",
			name: `${country.text} (Social Impact)`,
			link: "/socialimpact",
			country,
			description: "View social impact indicators for this country",
		}));

	// Contaminants (EFSA): Country entries
	const efsaCountryEntries = EFSA_COUNTRIES.map((countryName) => ({
		type: "efsa-country",
		name: `${countryName} (Contaminants)`,
		link: "/contaminants",
		countryName,
		description: "View food contaminant data for this country",
	}));

	// Disasters
	const disasterRouteEntry = {
		type: "disaster",
		name: "Disasters",
		link: "/disasters",
		description: "Climate disaster impact maps",
	};

	const disasterEventEntries = DISASTER_EVENT_TYPES.map((event) => ({
		type: "disaster-event",
		name: event.text,
		link: "/disasters",
		eventTypeText: event.text,
		description: `View ${event.text.toLowerCase()} impact data`,
	}));

	// LCA Indicator entries
	const generalLcaIndicatorEntries = LCA_INDICATORS.map((indicator) => ({
		type: "lca-indicator",
		name: indicator.label,
		link: "/socialimpact",
		description: indicator.definition,
	}));

	// Per-lab LCA/MAGNET/social entries
	const allIndicators = [
		...ENV_IMPACT_CATEGORIES.map((c) => ({ type: "env-category", name: c, description: "Environmental impact category", envCategory: c })),
		...SOCIAL_INDICATORS.map((s) => ({ type: "social-indicator", name: s, description: "Social impact indicator", socialCategory: SOCIAL_INDICATOR_CATEGORY_MAP[s] })),
		...SOCIAL_ENDPOINT_CATEGORIES.map((c) => ({ type: "social-category", name: c, description: "Social endpoint category", socialCategory: c })),
	];

	const labIndicatorEntries = labs.flatMap((lab) =>
		allIndicators.map((indicator) => ({
			type: indicator.type,
			name: indicator.name,
			link: lab.path,
			lab,
			description: indicator.description,
			...(indicator.envCategory && { envCategory: indicator.envCategory }),
			...(indicator.socialCategory && { socialCategory: indicator.socialCategory }),
		}))
	);

	return [
		...productEntries,
		...mapEntries,
		...subProductMapEntries,
		...labEntries,
		...uniqueRouteEntries,
		...indicatorEntries,
		...categoryEntries,
		...socialImpactCountryEntries,
		...efsaCountryEntries,
		disasterRouteEntry,
		...disasterEventEntries,
		...generalLcaIndicatorEntries,
		...labIndicatorEntries,
	];
};

// Module-level constant - built once at import time
const SEARCH_INDEX = buildSearchIndex();

export const useSearchData = () => SEARCH_INDEX;

export default useSearchData;
